import React, { useCallback, useEffect, useState } from 'react';
import Navbar from '../navbar/Navbar';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import ModalGenerateText from 'react-modal';
import ReviewsList from './components/ReviewsList';
import { CurrentFilters, Review } from './type';
import ReviewsHeader from './components/ReviewsHeader';
import ReviewBladeInfo from './components/ReviewBladeInfo';
import { useSocialNetworkData } from './utils/SocialNetworkData';
import { useOptimizedReviews } from './hooks/useOptimizedReviews';
import NoReviews from './components/NoReviews';
import ModalNotAllowed from './components/ModalNotAllowed';
import ModalResponse from './components/ModalResponse';
import FilterSorter from './components/FilterSorter';
import { checkFBPermissions } from './services/SocialNetworkService';
import { navigationFlag } from './services/SocialNetworkService';
import { useLocation } from 'react-router-dom';
import ReviewsHeaderSkeleton from './components/skeleton/ReviewsHeaderSkeleton';
import FilterSorterSkeleton from './components/skeleton/FilterSorterSkeleton';
import ReviewsListSkeleton from './components/skeleton/ReviewsListSkeleton';
import Pagination from '../pagination/Pagination';
import { REVIEWS_PER_PAGE } from './type';

ModalGenerateText.setAppElement('#root');

const Reviews: React.FC = () => {
    const userMyId = useSelector((state: RootState) => state.user.userMyId);
    const location = useLocation();

    const parseUrlFilters = () => {
        const searchParams = new URLSearchParams(location.search);
        const filters: CurrentFilters = { status: [], network: [] };

        const statusParam = searchParams.get('status');
        if (statusParam) {
            filters.status = statusParam.split(',');
        }

        const networkParam = searchParams.get('network');
        if (networkParam) {
            filters.network = networkParam.split(',');
        }

        return filters;
    };

    const initialFilters = location.search
        ? parseUrlFilters()
        : location.state?.filters || { status: [], network: [] };

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const [selectedReview, setSelectedReview] = useState<Review | null>(null);
    const [showFilter, setShowFilter] = useState<boolean>(false);
    const [showSorter, setShowSorter] = useState<boolean>(false);
    const [showIfNotAllowed, setShowIfNotAllowed] = useState<boolean>(false);
    const [isAllowed, setIsAllowed] = useState<boolean>(true);
    const [currentFilters, setCurrentFilters] = useState<CurrentFilters>(initialFilters);
    const [currentSort, setCurrentSort] = useState<string>('date_desc');
    const [content, setContent] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);

    const {
        reviews,
        platformRatings,
        isLoading,
        refreshReviews: refreshOptimizedReviews,
        syncCounts,
    } = useOptimizedReviews(userMyId || '');

    const { handleStatusUpdate } = useSocialNetworkData(userMyId || '', 0, true);

    useEffect(() => {
        navigationFlag.isNavigation = true;

        return () => {
            navigationFlag.isNavigation = false;
        };
    }, [location.pathname]);

    useEffect(() => {
        if (!userMyId) {
            return;
        }

        const verifyFacebookPermissions = async () => {
            const allowed = await checkFBPermissions(userMyId);
            setIsAllowed(allowed ?? true);
        };

        verifyFacebookPermissions();
    }, [userMyId]);

    useEffect(() => {
        if (!userMyId || reviews.length === 0) {
            return;
        }

        const timeoutId = setTimeout(() => {
            syncCounts();
        }, 2000);

        return () => clearTimeout(timeoutId);
    }, [userMyId, reviews.length, syncCounts]);

    const handleFilterChange = (filterType: 'status' | 'network', value: string) => {
        setCurrentPage(1);
        setCurrentFilters(prev => {
            const filters = { ...prev };
            if (filters[filterType].includes(value)) {
                filters[filterType] = filters[filterType].filter(v => v !== value);
            } else {
                filters[filterType] = [...filters[filterType], value];
            }
            return filters;
        });
    };

    const handleSortChange = (sortValue: string) => {
        setCurrentPage(1);
        setCurrentSort(sortValue);
    };

    const handleClearAllFilters = () => {
        setCurrentFilters({ status: [], network: [] });
    };

    const getFilteredAndSortedReviews = useCallback(() => {
        let filteredReviews = [...reviews];

        if (currentFilters.status.length > 0) {
            filteredReviews = filteredReviews.filter(review => {
                if (!review.status) return false;
                return currentFilters.status.includes(review.status.toLowerCase());
            });
        }

        if (currentFilters.network.length > 0) {
            filteredReviews = filteredReviews.filter(review =>
                currentFilters.network.includes(review.network),
            );
        }

        return [...filteredReviews].sort((a, b) => {
            const dateA = new Date(a.created_at).getTime();
            const dateB = new Date(b.created_at).getTime();
            return currentSort === 'date_desc' ? dateB - dateA : dateA - dateB;
        });
    }, [reviews, currentSort, currentFilters.status, currentFilters.network]);

    const filteredReviews = getFilteredAndSortedReviews();
    const totalPages = Math.ceil(filteredReviews.length / REVIEWS_PER_PAGE);

    const openModal = async (review: Review) => {
        if (userMyId) {
            setSelectedReview(review);

            if (!isAllowed && review?.network === 'facebook') {
                setShowIfNotAllowed(true);
            } else {
                setModalIsOpen(true);
            }
        }
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setSelectedReview(null);
        setContent('');
    };

    const toggleShowFilter = () => {
        if (showSorter) {
            setShowSorter(false);
        }
        setShowFilter(!showFilter);
    };

    const toggleShowSorter = () => {
        if (showFilter) {
            setShowFilter(false);
        }
        setShowSorter(!showSorter);
    };

    return (
        <div className="flex flex-1 relative w-full h-screen">
            <div className="flex-none relative min-w-64">
                <Navbar />
            </div>

            <div className="flex-1 relative">
                <div className="flex flex-col items-start relative h-full">
                    <div className="flex flex-col py-10 px-9 w-full h-full gap-[24px]">
                        <div>
                            <h1 className="text-dark-gray text-super-heavy font-heavy m-0 p-0">
                                Avis
                            </h1>
                            <p className="text-gray m-0 p-0">
                                Retrouvez l'ensemble des avis Google, Facebook et Opinion System
                                laissés par vos clients
                            </p>
                        </div>

                        {!isLoading && showIfNotAllowed && (
                            <ModalNotAllowed
                                isLoading={isLoading}
                                setShowIfNotAllowed={setShowIfNotAllowed}
                            />
                        )}

                        {isLoading ? (
                            <ReviewsHeaderSkeleton />
                        ) : (
                            <ReviewsHeader
                                platformRatings={platformRatings}
                                isLoading={false}
                                onRefresh={refreshOptimizedReviews}
                            />
                        )}

                        {!isLoading && reviews.length !== 0 && (
                            <div className="w-full border-t border-light-gray mx-auto" />
                        )}

                        {isLoading ? (
                            <FilterSorterSkeleton />
                        ) : reviews.length > 0 ? (
                            <FilterSorter
                                onFilterChange={handleFilterChange}
                                onSortChange={handleSortChange}
                                showFilter={showFilter}
                                showSorter={showSorter}
                                toggleShowFilter={toggleShowFilter}
                                toggleShowSorter={toggleShowSorter}
                                currentFilters={currentFilters}
                                currentSort={currentSort}
                                platformRatings={platformRatings}
                            />
                        ) : null}

                        <div className="m-0 w-full flex flex-col gap-[20px]">
                            <div className="m-0 flex flex-wrap gap-[20px]">
                                {isLoading ? (
                                    <ReviewsListSkeleton />
                                ) : userMyId ? (
                                    <ReviewsList
                                        reviews={filteredReviews}
                                        openModal={openModal}
                                        isLoading={false}
                                        onReviewsUpdate={handleStatusUpdate}
                                        currentPage={currentPage}
                                        setCurrentPage={setCurrentPage}
                                    />
                                ) : null}
                            </div>

                            {!isLoading && filteredReviews.length > 0 && (
                                <Pagination
                                    firstPage={() => setCurrentPage(1)}
                                    lastPage={() => setCurrentPage(totalPages)}
                                    currentPage={currentPage}
                                    totalPage={totalPages}
                                    previous={() => setCurrentPage(p => p - 1)}
                                    next={() => setCurrentPage(p => p + 1)}
                                />
                            )}

                            {!isLoading && reviews.length <= 2 && (
                                <div className="w-full border-t border-light-gray mx-auto" />
                            )}

                            {reviews.length <= 2 && <ReviewBladeInfo isLoading={isLoading} />}

                            {!isLoading && filteredReviews.length === 0 && reviews.length > 0 && (
                                <div className="text-center py-8">
                                    <p className="text-gray-500 text-lg">
                                        Aucun avis ne correspond aux filtres sélectionnés.
                                    </p>
                                    <button
                                        onClick={handleClearAllFilters}
                                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                    >
                                        Effacer les filtres
                                    </button>
                                </div>
                            )}

                            {!isLoading && reviews.length === 0 && (
                                <>
                                    <div className="w-full border-t border-light-gray mx-auto" />
                                    <NoReviews isLoading={isLoading} />
                                </>
                            )}

                            <ModalResponse
                                isOpen={modalIsOpen}
                                onClose={closeModal}
                                selectedReview={selectedReview}
                                setContent={setContent}
                                content={content}
                                setRefreshReviews={() => refreshOptimizedReviews()}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Reviews;
